import { CtaState } from "~/components/denomination_grid/denominations_grid";
import { useAnalyticsService } from "~/context/analytics_service_context";
import { HubbleEvent } from "~/data/events";
import { clientRepo } from "~/server/apis/client_repo";
import { VoucherProduct } from "~/server/types/brand";
import {
  collectPaymentViaRazorpayCustomCheckout,
  RazorpayCheckoutProps,
  RazorpayMode,
} from "~/utils/third_party/razorpay";
import { showSnackBar } from "~/shared_states/snackbar";
import { useWebStoreContext } from "~/context/webstore_context";
import { createSignal } from "solid-js";
import { Header } from "~/types";
import { captureErrorInSentry } from "~/utils/third_party/sentry";
import { ErrorMessage } from "~/data/consts";
import { generateDeviceVerificationToken } from "~/utils/common";
import { collectCardDetails } from "~/components/card_details_form";
import { useNavigate } from "@solidjs/router";
import { openOrderFailedModal } from "~/components/webstore_telescope/order_failed_modal";
import { onecardCardValidator } from "~/views/webstore/brandl2_onecard/utils/onecard_card_validator";
import useUserInfoModal from "~/hooks/useUserInfoModal";
import { config } from "~/data/config";

export function useOnecardPayment(props: {
  brand: () => VoucherProduct;
  brandKey: string;
}) {
  const [ctaState, setCtaState] = createSignal<CtaState>("DEFAULT");
  const [showFullPageLoadingOverlay, setShowFullPageLoadingOverlay] =
    createSignal(false);
  const navigate = useNavigate();

  const brand = props.brand;

  const analyticsService = useAnalyticsService();
  const { storeState, refreshV4HomePage, getPartnerConfigV3 } =
    useWebStoreContext();
  const userInfoModal = useUserInfoModal();

  const shouldCollectUserData = () => {
    const user = storeState.v4HomePage?.user;
    if (!user) return false;
    const isPhoneMissing = !user.phoneNumber;
    const isEmailMissing = !user.email;
    if (
      (isPhoneMissing || isEmailMissing) &&
      (getPartnerConfigV3().showUserDataCollectionModal ||
        storeState.routeData?.clientConfig?.businessModel === "B2C")
    ) {
      return true;
    }
    return false;
  };

  function fullUserName() {
    const user = storeState.v4HomePage?.user;
    return `${user?.firstName ?? ""} ${user?.lastName ?? ""}`.trim();
  }

  type OnClickGetThisCardProps = {
    amount: number;
    variantId: string | null;
    consumeCoins: boolean;
    denominationDetails?: { denomination: number; quantity: number }[];
  };

  async function onClickGetThisCard(props: OnClickGetThisCardProps) {
    if (shouldCollectUserData()) {
      return triggerUserDataCollectionFlow({
        onComplete: async () => {
          return onClickGetThisCard(props);
        },
      });
    }
    await initiateOrder(props);
  }

  function triggerUserDataCollectionFlow(props: { onComplete: () => void }) {
    userInfoModal.openModal({
      clientLogoUrl: storeState.routeData?.clientConfig?.clientLogoUrl,
      brandDetails: brand()?.voucherProductMetadata,
      email: storeState.v4HomePage?.user?.email ?? null,
      phone: storeState.v4HomePage?.user?.phoneNumber ?? null,
      name: fullUserName(),
      collectName: false,
      onClickSubmit: async (profileUpdateParams: any) => {
        analyticsService.logEvent("submit_user_details_in_brandl2", {
          brandName: brand()?.name.toLowerCase(),
          ...profileUpdateParams,
        });

        userInfoModal.setShowLoader(true);
        try {
          await clientRepo.updatePartnerUserProfile(profileUpdateParams);
          await refreshV4HomePage();
          await userInfoModal.closeModal();
          props.onComplete();
        } catch (e: any) {
          showSnackBar({
            level: "error",
            message: e.message ?? "Something went wrong",
          });
        } finally {
          userInfoModal.setShowLoader(false);
        }
      },
      subtitle: "Gift card details will be sent on your email",
    });
  }

  const initiateOrder = async (orderProps: OnClickGetThisCardProps) => {
    const { amount, variantId, consumeCoins, denominationDetails } = orderProps;
    try {
      setCtaState("LOADING");

      let headers: Record<string, any> = {};
      if (storeState.v4HomePage?.user.fraudChecksEnabled) {
        const token = await generateDeviceVerificationToken();
        if (!token) {
          const err = new Error("FPJS token is undefined");
          captureErrorInSentry(err);
          showSnackBar({
            message: ErrorMessage.SECURITY_CHECK_FAILED,
            level: "error",
          });
          throw err;
        }
        headers[Header.FpDeviceToken] = token;
      }

      const initiateRedemptionResponse = await clientRepo.initiateRedemption(
        {
          amount: amount,
          paymentMode: "UPI",
          productId: brand()?.externalId!,
          ...(denominationDetails != null ? { denominationDetails } : {}),
          variantId: variantId ?? undefined,
          consumeCoins: consumeCoins,
        },
        headers
      );

      analyticsService.logEvent(HubbleEvent.PAYMENT_INITIATED, {
        brandName: brand()!.name.toLowerCase(),
        amount: amount,
        consumeCoins: consumeCoins,
        productId: brand()!.externalId,
      });

      function onPaymentFailed(failureReason: string) {
        analyticsService.logEvent(HubbleEvent.PAYMENT_FAIL, {
          brandName: brand()!.name.toLowerCase(),
          amount: amount,
          reason: failureReason,
        });

        openOrderFailedModal({
          reason: failureReason,
        });
      }

      function onPaymentComplete() {
        analyticsService.logEvent(HubbleEvent.PAYMENT_SUCCESS, {
          brandName: brand()!.name.toLowerCase(),
          amount: amount,
        });

        const orderId = encodeURIComponent(
          initiateRedemptionResponse.storeOrderDetails.id
        );
        const brandKeyEncoded = encodeURIComponent(brand()!.brandKey);

        navigate(`/buy-gift-card/${brandKeyEncoded}/order?orderId=${orderId}`, {
          resolve: true,
        });
      }

      async function collectPaymentViaRazorpay(_props: {
        depositId: string;
        amount: number;
      }) {
        const order = await clientRepo.payOrderStandardCheckout(
          _props.depositId
        );

        const razorpayCheckoutParams: RazorpayCheckoutProps = {
          amount: _props.amount,
          email: storeState.v4HomePage?.user?.email ?? "",
          orderId: order
            ? order.providerOrderId
            : (initiateRedemptionResponse?.depositPaymentDetails
                .providerMetadata?.providerOrderId ?? ""),
          phone: storeState.v4HomePage?.user?.phoneNumber ?? "",
          onSuccess: onPaymentComplete,
          onFailure: (response) => {
            onPaymentFailed(response.error.description);
          },
          // using same rzp key for onecard as it is used for tata digital
          key: config.tdSubscriptionRazorpayKey,
          mode: "CARD" as RazorpayMode,
          config: {
            prefill: {
              email: storeState.v4HomePage?.user?.email ?? "",
              contact: storeState.v4HomePage?.user?.phoneNumber ?? "",
              method: "card",
            },
          },
          customer_id:
            initiateRedemptionResponse?.depositPaymentDetails.providerMetadata
              ?.customerId ?? "",
        };

        collectCardDetails({
          onSubmit: async (cardDetails) => {
            const event_params: Record<string, any> = {
              ...razorpayCheckoutParams,
              brandName: brand()?.name.toLowerCase(),
            };
            delete event_params.onSuccess;
            delete event_params.onFailure;

            analyticsService.logEvent(
              "collect_card_details_in_brandl2",
              event_params
            );
            setShowFullPageLoadingOverlay(true);
            collectPaymentViaRazorpayCustomCheckout({
              ...razorpayCheckoutParams,
              cardDetails: cardDetails,
            });
          },
          cardNumberLiveValidator: onecardCardValidator,
          title: "Enter your OneCard credit card details",
        });
      }

      if (
        initiateRedemptionResponse.depositPaymentDetails.status == "SUCCESS"
      ) {
        onPaymentComplete();
      } else {
        await collectPaymentViaRazorpay({
          depositId: initiateRedemptionResponse.depositPaymentDetails.id,
          amount: initiateRedemptionResponse.depositPaymentDetails.amount,
        });
      }
      setCtaState("DEFAULT");
    } catch (error: any) {
      showSnackBar({
        message: error.message,
        level: "error",
      });
      setCtaState("DEFAULT");
    }
  };

  return {
    onClickGetThisCard,
    getDenominationList: () => undefined,
    showFullPageLoadingOverlay,
    ctaState,
    setCtaState,
  };
}
